<template>
    <li
        :class="[
            $style['menu-item'],
            first ? $style['menu-item-first'] : '',
            last ? $style['menu-item-last'] : '',
            mobile ? $style['mobile-item'] : $style['desktop-item'],
            dropdown ? $style['with-dropdown-items'] : '',
            dropdownItem && showSubItemsInColumn
                ? $style['column-list-item']
                : '',
            dropdownItem ? $style['dropdown-item'] : '',
            columnItem ? $style['column-item'] : '',
            expanded ? $style['expanded'] : $style['collapsed'],
            bold ? $style['bold'] : '',
            smaller ? $style['smaller'] : '',
            isBlank(menuItem) ? $style['blank'] : '',
            menuItem?.customClassName,
        ]"
        :data-level="level"
    >
        <component
            :is="isClickable ? 'a' : 'span'"
            :href="link"
            @click="clickLink($event)"
            :class="$style['menu-item-link']"
            :target="target"
        >
            <img
                v-if="menuItem?.iconUrl != null"
                :src="menuItem.iconUrl"
                :alt="menuItem.name"
                data-no-block
                :class="$style['menu-item-icon']"
                :width="menuItem.iconWidth"
                :height="menuItem.iconHeight"
            />
            <span :class="$style['menu-item-text']"
                ><template
                    v-for="entry in extractEmojiData(
                        menuItem?.name ?? name ?? ''
                    )"
                    ><template v-if="entry.type === 'text'">{{
                        entry.content
                    }}</template
                    ><template v-else-if="entry.type == 'emoji'"
                        ><img
                            :src="createNotoImgUrlForSymbol(entry.content)"
                            width="16"
                            height="16"
                            data-no-block
                            style="vertical-align: -0.125em"
                            :alt="entry.content" /></template></template
                ><template v-if="dropdown && !mobile"
                    >&nbsp;&gt;</template
                ></span
            >
            <span
                :class="[
                    $style['expand-indicator'],
                    expanded ? $style['expanded'] : $style['collapsed'],
                ]"
                v-if="dropdown"
                >&#x276F;</span
            >
            <span
                :class="[$style['item-count']]"
                v-if="
                    !dropdown &&
                    !columnItem &&
                    !showSubItemsInColumn &&
                    menuItem?.count != null
                "
                >({{ menuItem.count }})</span
            >
        </component>

        <FadeAndSlide v-if="!disableItems">
            <template
                v-if="
                    menuItem?.items != null &&
                    menuItem.items.length > 0 &&
                    ((dropdown && expanded) ||
                        (dropdownItem && showSubItemsInColumn))
                "
            >
                <Items
                    :dropdown="dropdown"
                    :mobile="mobile"
                    :parent-item="menuItem"
                    :menu-items="menuItem.items"
                    :parent-last="last"
                    :show-as-column="dropdownItem && showSubItemsInColumn"
                    :one-per-column-layout="
                        !mobile &&
                        dropdown &&
                        isMulticolumnDropdownContainer(menuItem)
                    "
                    :level="level + 1"
                ></Items>
            </template>
        </FadeAndSlide>
    </li>
</template>

<style module lang="scss">
.menu-item {
    .expand-indicator {
        display: none;
    }

    .item-count {
        margin-left: auto;
        font-size: 11px;
    }

    &.bold {
        font-weight: bold;
    }

    &.mobile-item {
        padding-top: 10px;
        padding-bottom: 10px;
        border-top: 1px solid #979797;
        font-size: 14px;
        line-height: 16px;

        &.blank {
            display: none;

            + .mobile-item {
                border-top-width: 2px;
            }
        }

        .item-count {
            margin-right: 0.75em;
        }

        &.menu-item-last {
            border-bottom: 1px solid #979797;
        }

        &.dropdown-item:last-child:not(.menu-item-last) {
            padding-bottom: 0;
        }

        &.smaller {
            font-size: 12px;
        }

        &.with-dropdown-items {
            &.expanded {
                > .menu-item-text,
                > .menu-item-link > .menu-item-text {
                    font-weight: bold;
                }
            }
        }

        .expand-indicator {
            display: inline-block;
            margin-left: auto;
            margin-right: 0.75em;

            transition: transform 400ms ease-out;

            &.collapsed {
                transform: rotateY(0deg);
            }
            &.expanded {
                transform: rotateY(-180deg);
            }
        }
    }

    &.desktop-item {
        margin-top: -25px;
        margin-right: 0;
        margin-bottom: -25px;
        margin-left: 0;
        padding-top: 13px;
        padding-right: 15px;
        padding-bottom: 13px;
        padding-left: 15px;
        font-size: 14px;
        line-height: 18px;

        &[data-level="0"] {
            > .menu-item-link {
                font-weight: 600;
            }
        }

        &:global(.pull-right-desktop) {
            margin-left: auto;
            margin-right: 15px;
        }

        .dropdown-item {
            margin-top: 0.125em;
            margin-right: 15px;
            margin-bottom: 0.125em;
            margin-left: 15px;
            padding-top: 0;
            padding-right: 0;
            padding-bottom: 0;
            padding-left: 0;
        }

        &.column-item {
            margin-top: 0;
            margin-bottom: 0;
            padding-top: 5px;
            padding-bottom: 5px;
            padding-left: 0;
            padding-right: 0;
        }

        &.with-dropdown-items {
            &.expanded {
                background-color: rgba(255, 255, 255, 0.39);
            }
        }

        &.column-list-item {
            flex: 1;
            > .menu-item-link {
                white-space: nowrap;
                font-size: 15px;
                text-transform: uppercase;
                margin-bottom: 15px;
                font-weight: bold;
            }
        }

        &.menu-item-first:not(.dropdown-item) {
            margin-left: 0;
        }

        &.menu-item-first:not(.dropdown-item):not(.column-item) {
            margin-left: -15px;
        }

        &.menu-item-last:not(.dropdown-item) {
            margin-right: 0;
        }
    }
}

.menu-item-icon {
    width: 16px;
    margin-right: 5px;
}

.menu-item-link {
    display: flex;
    flex-direction: row;
    align-items: center;
}
</style>

<script setup lang="ts">
import { type PropType, computed } from "vue";
import {
    isDropdownContainer,
    isMulticolumnDropdownContainer,
    type MenuItem,
    type PatchedMenuItem,
} from "./menu-item";
import Items from "./items.vue";
import FadeAndSlide from "../../shared/FadeAndSlide.vue";
import { isNullOrWhitespace } from "../../../../shared/utils/string";
import { createNotoImgUrlForSymbol, extractEmojiData } from "./patch-emoji";

const props = defineProps({
    menuItem: {
        type: Object as PropType<PatchedMenuItem>,
    },
    mobile: Boolean,
    pseudo: Boolean,
    first: Boolean,
    last: Boolean,
    dropdownItem: Boolean,
    columnItem: Boolean,
    showSubItemsInColumn: Boolean,
    expanded: Boolean,
    bold: Boolean,
    smaller: Boolean,
    name: String,
    disableItems: Boolean,
    level: {
        type: Number,
        required: true,
    },
});

const dropdown = computed(() => {
    return (
        !props.disableItems &&
        !props.showSubItemsInColumn &&
        isDropdownContainer(props.menuItem, props.mobile)
    );
});

const link = computed(() => {
    if (dropdown.value) {
        return "#";
    }
    return props.menuItem?.link ?? "#";
});

const isClickable = computed(
    () =>
        dropdown.value ||
        (typeof props.menuItem?.link == "string" &&
            props.menuItem.link.trim() != "")
);

const target = computed(() => {
    return props.menuItem != null && !isNullOrWhitespace(props.menuItem.target)
        ? props.menuItem.target
        : undefined;
});

const emit = defineEmits(["click", "expand", "collapse"]);

function isBlank(menuItem: MenuItem | undefined) {
    // return false, because undefined means filler item
    if (menuItem == null) return false;
    if (menuItem.name.trim() == "") {
        return true;
    }
    return false;
}

function clickLink(event: Event) {
    if (props.pseudo) {
        emit("click");
        event.preventDefault();
        return;
    }
    if (dropdown.value) {
        if (props.expanded) {
            emit("collapse");
        } else {
            emit("expand");
        }
        event.preventDefault();
        return;
    }
}
</script>
